import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { SalesGroupModel, SourceModel } from '@wolf/entities'
import { trpc } from './utils/trpc'
import { URL_PARAMS } from './utils/url-params'

type SalesGroupFilterParams = {
  salesGroupId?: SalesGroupModel['salesGroupId']
  fromDate?: SalesGroupModel['fromDate']
  toDate?: SalesGroupModel['toDate']
}

type SourceParams = {
  serialId?: SourceModel['serialId']
  name?: SourceModel['name']
  language?: SourceModel['language']
  isRetail?: boolean
}

type GlobalFilterResponse = {
  salesGroupFilterParams: SalesGroupFilterParams
  sourceFilterParams: SourceParams
  changeSalesGroupParams: (params: { salesGroupId: SalesGroupFilterParams['salesGroupId']; replace?: boolean }) => void
  changeSourceParams: (params: { serialId: SourceParams['serialId']; replace?: boolean }) => void
}

export const useUrlGlobalFilterParams = (): GlobalFilterResponse => {
  const [searchParams, setSearchParams] = useSearchParams()

  // Memoize the merge function to avoid recreating it on every render
  const mergeParams = useCallback((newParams: URLSearchParams) => {
    const updatedParams = new URLSearchParams(searchParams)
    newParams.forEach((value, key) => {
      if (value) {
        updatedParams.set(key, value)
      } else {
        updatedParams.delete(key)
      }
    })
    return updatedParams
  }, [searchParams])

  // Memoize the change functions to avoid recreating them on every render
  const changeSalesGroupParams = useCallback(({
    salesGroupId,
    replace,
  }: { salesGroupId: SalesGroupFilterParams['salesGroupId']; replace?: boolean }) => {
    const newParams = new URLSearchParams()
    if (salesGroupId) {
      newParams.set(URL_PARAMS.SALES_GROUP_ID, salesGroupId.toString())
    }
    const mergedParams = mergeParams(newParams)
    setSearchParams(mergedParams, { replace: replace || false })
  }, [mergeParams, setSearchParams])

  const changeSourceParams = useCallback(({
    serialId,
    replace
  }: { serialId: SourceParams['serialId']; replace?: boolean }) => {
    const newParams = new URLSearchParams()
    if (serialId) {
      newParams.set(URL_PARAMS.SOURCE_ID, serialId.toString())
    }
    const mergedParams = mergeParams(newParams)
    setSearchParams(mergedParams, { replace: replace || false })
  }, [mergeParams, setSearchParams])

  // Memoize the extracted values from URL to avoid recalculating on every render
  const salesGroupId = useMemo(() => {
    return searchParams.get(URL_PARAMS.SALES_GROUP_ID)
      ? Number(searchParams.get(URL_PARAMS.SALES_GROUP_ID))
      : undefined
  }, [searchParams])

  const sourceId = useMemo(() => {
    return searchParams.get(URL_PARAMS.SOURCE_ID)
      ? Number(searchParams.get(URL_PARAMS.SOURCE_ID))
      : undefined
  }, [searchParams])

  const { data: salesGroup } = trpc.salesGroups.getSalesGroup.useQuery(
    {
      salesGroupId: Number(salesGroupId),
      sourceId
    },
    {
      enabled: !!salesGroupId,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5 // 5 minutes
    }
  )
  const { data: source } = trpc.sources.getSource.useQuery(Number(sourceId),
    {
      enabled: !!sourceId,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5 // 5 minutes
    }
  )

  // Memoize the return values to avoid recreating objects on every render
  const salesGroupFilterParams = useMemo(() => ({
    salesGroupId,
    fromDate: salesGroup ? salesGroup.fromDate : undefined,
    toDate: salesGroup ? salesGroup.toDate : undefined,
  }), [salesGroupId, salesGroup])

  const sourceFilterParams = useMemo(() => ({
    serialId: sourceId,
    name: source?.name,
    language: source?.language,
    isRetail: source?.isRetail ?? undefined,
  }), [sourceId, source])

  return {
    salesGroupFilterParams,
    sourceFilterParams,
    changeSalesGroupParams,
    changeSourceParams
  }
}
